import React, { useState, useEffect, useMemo } from 'react';
import ReactECharts from "echarts-for-react";
import { Icon } from '@iconify/react';
import { useApi } from './../../hooks/useApi';
import { usePermissions } from './../../hooks/usePermissions';

export default function Resumen() {

    const { get, loading } = useApi();
    const { perfil } = usePermissions();

    const today = new Date().toISOString().split("T")[0];

    const [tipo, setTipo] = useState(2);
    const [fecha, setFecha] = useState(today);
    const [horas, setHoras] = useState([]);
    const [ventas, setVentas] = useState([]);
    const [ventasSAP, setVentasSAP] = useState([]);
    const [procesadasSAP, setProcesadasSAP] = useState([]);
    const [pendientesSAP, setPendientesSAP] = useState([]);

    const [ventasPorTienda, setVentasPorTienda] = useState([]);

    const obtenerVentasHoy = async () => {
        try {
            const data = await get('resumenVentasHoy', {
                params: {
                    applicationId: tipo,
                    fecha: fecha
                }
            });

            const mockVentas = [
                { hora: 9, cantidad: 15, enviadosSAP: 10, procesadosSAP: 8 },
                { hora: 10, cantidad: 20, enviadosSAP: 18, procesadosSAP: 15 },
                { hora: 11, cantidad: 28, enviadosSAP: 25, procesadosSAP: 22 },
                { hora: 12, cantidad: 35, enviadosSAP: 30, procesadosSAP: 29 },
                { hora: 13, cantidad: 40, enviadosSAP: 38, procesadosSAP: 36 },
                { hora: 14, cantidad: 32, enviadosSAP: 30, procesadosSAP: 28 },
                { hora: 15, cantidad: 50, enviadosSAP: 48, procesadosSAP: 44 }
            ];

            const usarMock = true;

            const ventasHoy = usarMock
                ? mockVentas
                : (data?.ventasTiendas ?? []);

            if (!ventasHoy.length) {
                setHoras([]);
                setVentas([]);
                setVentasSAP([]);
                setProcesadasSAP([]);
                setPendientesSAP([]);
                return;
            }

            const ordenado = [...ventasHoy].sort((a, b) => a.hora - b.hora);

            const horasFormateadas = ordenado.map(x =>
                `${x.hora.toString().padStart(2, '0')}:00`
            );

            const ventasTotales = ordenado.map(x => x.cantidad ?? 0);
            const enviados = ordenado.map(x => x.enviadosSAP ?? 0);
            const procesados = ordenado.map(x => x.procesadosSAP ?? 0);

            // 🔥 pedidos pendientes de procesar en SAP
            const pendientes = ordenado.map(x =>
                (x.cantidad ?? 0) - (x.procesadosSAP ?? 0)
            );

            setHoras(horasFormateadas);
            setVentas(ventasTotales);
            setVentasSAP(enviados);
            setProcesadasSAP(procesados);
            setPendientesSAP(pendientes);

            setVentasPorTienda(data?.ventasHistoricas ?? []);

        } catch (error) {
            console.error("Error cargando ventas hoy:", error);
        }
    };

    const obtenerVentasHoy2 = async () => {
        try {
            const data = await get('resumenVentasHoy', {
                params: {
                    applicationId: tipo,
                    fecha: fecha
                }
            });
            const ventasHoy = data?.ventasTiendas ?? [];

            if (!ventasHoy.length) {
                setHoras([]);
                setVentas([]);
            } else {
                const ordenado = [...ventasHoy].sort((a, b) => a.hora - b.hora);

                setHoras(
                    ordenado.map(x =>
                        `${x.hora.toString().padStart(2, '0')}:00`
                    )
                );

                setVentas(ordenado.map(x => x.cantidad));
                setVentasSAP(ordenado.map(x => x.enviadosSAP));
                setProcesadasSAP(ordenado.map(x => x.procesadosSAP));

            }

            setVentasPorTienda(data?.ventasHistoricas ?? []);

        } catch (error) {
            console.error("Error cargando ventas hoy:", error);
        }
    };

    const kpis = useMemo(() => {

        const totalVentas = ventasPorTienda.reduce((acc, t) => acc + (t.total || 0), 0);
        const totalTickets = ventasPorTienda.reduce((acc, t) => acc + (t.cont || 0), 0);
        const ticketPromedio = totalTickets ? totalVentas / totalTickets : 0;

        const tiendaTop = [...ventasPorTienda]
            .sort((a, b) => b.total - a.total)[0];

        return {
            totalVentas,
            totalTickets,
            ticketPromedio,
            tiendaTop
        };

    }, [ventasPorTienda]);

    useEffect(() => {
        obtenerVentasHoy();
    }, []);

    const optionVentasHoy = useMemo(() => ({
        color: ['#2563EB', '#F59E0B', '#10B981'],
        title: {
            text: "Ventas del día",
            left: "center"
        },
        tooltip: {
            trigger: 'axis'
        },
        legend: {
            bottom: 0
        },
        grid: {
            top: 60,
            left: 50,
            right: 30,
            bottom: 40
        },
        animationDuration: 800,
        animationEasing: 'cubicOut',
        xAxis: {
            type: 'category',
            data: horas
        },
        yAxis: {
            type: 'value'
        },
        toolbox: {
            feature: {
            dataView: { show: true, readOnly: false },
            magicType: { show: true, type: ['line', 'bar'] },
            restore: { show: true },
            saveAsImage: { show: true }
            }
        },
        series: [{
            name: 'Ventas',
            type: 'line',
            smooth: true,
            showSymbol: false,
            symbolSize: 8,
            areaStyle: { opacity: 0.2 },
            lineStyle: { width: 3 },
            itemStyle: {
                color: '#1261B5'
            },
            data: ventas
        },
        {
            name: 'Enviadas SAP',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: {
                width: 3,
                type: 'dashed'   
            },
            itemStyle: {
                color: '#170bf5'
            },
            data: ventasSAP
        },
        {
            name: 'Procesadas SAP',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            data: procesadasSAP,
            showSymbol: false,
            itemStyle: {
                color: '#3BBF72'
            },
        },
        {
            name: 'Pendientes SAP',
            type: 'line',
            smooth: true,
            symbol: 'none',
            lineStyle: {
                width: 0,
                color: '#DC2626'   
            },
            areaStyle: {
                color: '#DC2626',
                opacity: 0.35
            },
            data: pendientesSAP
        }]
    }), [horas, ventas,ventasSAP, procesadasSAP]);

    /* =========================
       🏆 TOP 10 POR VENTAS
    ========================== */
    const topTiendas = useMemo(() =>
        [...ventasPorTienda]
            .sort((a, b) => b.cont - a.cont)
            .slice(0, 10),
        [ventasPorTienda]
    );

    const optionTopTiendas = useMemo(() => ({
        title: {
            text: 'Top 10 Tiendas con más ventas',
            left: 'center'
        },
        tooltip: { trigger: 'axis' },
        grid: { top: 60, left: 120, right: 40, bottom: 30 },
        xAxis: { type: 'value' },
        yAxis: {
            type: 'category',
            data: topTiendas.map(t => t.storeKey),
            inverse: true
        },
        series: [{
            type: 'bar',
            data: topTiendas.map(t => t.cont),
            label: {
                show: true,
                position: 'right'
            },
            itemStyle: {
                borderRadius: [0, 6, 6, 0]
            }
        }]
    }), [topTiendas]);

    /* =========================
       💰 TOP 10 POR INGRESO
    ========================== */
    const topTiendasDinero = useMemo(() =>
        [...ventasPorTienda]
            .sort((a, b) => b.total - a.total)
            .slice(0, 10),
        [ventasPorTienda]
    );

    const optionTopDinero = useMemo(() => ({
        title: {
            text: 'Top 10 Tiendas con mayor ingreso',
            left: 'center'
        },
        tooltip: {
            trigger: 'item',
            formatter: '{b}<br/>${c} ({d}%)'
        },
        legend: { bottom: 0 },
        series: [{
            type: 'pie',
            radius: ['45%', '70%'],
            animationDuration: 800,
            itemStyle: {
                borderRadius: 6,
                borderColor: '#fff',
                borderWidth: 3
            },
            label: {
                show: true,
                position: 'inside',
                formatter: '{d}%',
                fontSize: 13,
                color: '#fff'
            },
            data: topTiendasDinero.map(t => ({
                value: t.total,
                name: t.storeKey
            }))
        }]
    }), [topTiendasDinero]);

    return (
        <div className="space-y-6">

            {/* HEADER */}
            {/*
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h3 className="text-xl font-semibold text-gray-800">
                        Dashboard
                    </h3>
                    <p className="text-sm text-gray-500">
                        Financial Dashboard
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <select
                        value={tipo}
                        onChange={(e) => setTipo(Number(e.target.value))}
                        className="border rounded-lg px-3 py-2 text-sm">
                        <option value={2}>Puntos de Venta</option>
                        <option value={3}>Qmovil-E</option>
                        <option value={1}>Otro</option>
                    </select>

                    <input
                        value={fecha}
                        onChange={(e) => setFecha(e.target.value)}
                        type="date"
                        className="border rounded-lg px-3 py-2 text-sm"
                    />

                    <button
                        onClick={obtenerVentasHoy}
                        disabled={loading}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition">
                        {loading ? "Consultando..." : "Consultar"}
                    </button>
                </div>
            </div> --*/}
            <div className="flex flex-wrap items-center justify-between gap-6">

                {/* KPIs */}
                <div className="flex flex-wrap gap-2 text-sm">

                    <div className="flex items-center gap-1 bg-green-50 px-4 py-2 bg-white/70 backdrop-blur rounded-xl hover:shadow-md transition">
                        <div className="bg-green-200 text-green-600 p-2 rounded-lg">
                            <Icon icon="mdi:cash-multiple" width={20} />
                        </div>

                        <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Ventas</p>
                            <p className="font-bold text-gray-800 text-lg">
                                {kpis.totalVentas.toLocaleString('es-MX', {
                                    style: 'currency',
                                    currency: 'MXN'
                                })}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-1 bg-blue-50 px-4 py-2 rounded-xl hover:shadow-md transition">

                        <div className="bg-blue-100 text-blue-600 p-2 rounded-lg">
                            <Icon icon="mdi:receipt-text" width={20} />
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Tickets</p>
                            <p className="font-bold text-gray-800 text-lg">
                                {kpis.totalTickets.toLocaleString('es-MX')}
                            </p>
                        </div>

                    </div>

                    <div className="flex items-center gap-1 bg-yellow-50 px-4 py-2 rounded-xl hover:shadow-md transition">
                        <div className="bg-yellow-100 text-yellow-600 p-2 rounded-lg">
                            <Icon icon="mdi:trophy" width={20} />
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Top tienda</p>
                            <p className="font-bold text-gray-800 text-sm">
                                {kpis.tiendaTop?.storeKey || "-"}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 bg-[#EAF6FF] px-4 py-2 rounded-xl hover:shadow-md transition">
                        <div className="bg-white text-[#0A6ED1] p-2 rounded-lg shadow-sm">
                            <Icon icon="simple-icons:sap" width={18} />
                        </div>
                        <div>
                            <p className="text-xs text-[#0A6ED1] uppercase tracking-wide font-semibold">
                                ENV SAP
                            </p>
                            <p className="font-bold text-gray-800 text-sm">
                                100
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 bg-[#EAF6FF] px-4 py-2 rounded-xl hover:shadow-md transition">
                        <div className="bg-white text-[#0A6ED1] p-2 rounded-lg shadow-sm">
                            <Icon icon="simple-icons:sap" width={18} />
                        </div>
                        <div>
                            <p className="text-xs text-[#0A6ED1] uppercase tracking-wide font-semibold">
                                REC SAP
                            </p>
                            <p className="font-bold text-gray-800 text-sm">
                                98
                            </p>
                        </div>
                    </div>

                </div>

                {/* FILTROS */}
                <div className="flex flex-wrap items-center gap-1">
                    <select
                        value={tipo}
                        onChange={(e) => setTipo(Number(e.target.value))}
                        className="border rounded-lg px-3 py-2 text-sm">
                        <option value={1}>Puntos de Venta</option>
                        <option value={2}>Qmovil-E</option>
                        <option value={3}>Otro</option>
                    </select>

                    <input
                        value={fecha}
                        onChange={(e) => setFecha(e.target.value)}
                        type="date"
                        className="border rounded-lg px-3 py-2 text-sm"
                    />

                    <button
                        onClick={obtenerVentasHoy}
                        disabled={loading}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition">
                        {loading ? "Consultando..." : "Consultar"}
                    </button>
                </div>

            </div>

            {loading && (
                <div className="text-center text-gray-500">
                    Cargando datos...
                </div>
            )}

            {/* PRINCIPAL */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-xl shadow md:col-span-2">
                    <ReactECharts option={optionVentasHoy} style={{ height: 380 }} />
                </div>

                <div className="bg-white p-4 rounded-xl shadow">
                    <table className="w-full text-xs border-separate border-spacing-y-1">
                        <thead className="bg-gray-200">
                            <tr>
                                <th className="px-2 py-1 text-left">Tienda</th>
                                <th className="px-2 py-1 text-right">Ventas</th>
                                <th className="px-2 py-1 text-right">Monto</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ventasPorTienda.map((item, index) => (
                                <tr key={index} className="bg-gray-50 hover:bg-gray-100">
                                    <td className="px-2 py-1">{item.storeDescription}</td>
                                    <td className="px-2 py-1 text-right font-semibold">
                                        {item.cont}
                                    </td>
                                    <td className="px-2 py-1 text-right font-semibold">
                                        {(item.total ?? 0).toLocaleString('es-MX', {
                                            style: 'currency',
                                            currency: 'MXN'
                                        })}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* SECUNDARIAS */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-xl shadow">
                    <ReactECharts option={optionTopTiendas} style={{ height: 400 }} />
                </div>
                <div className="bg-white p-4 rounded-xl shadow">
                    <ReactECharts option={optionTopDinero} style={{ height: 380 }} />
                </div>
            </div>

        </div>
    );
}